import React, { useEffect, useState, useCallback } from "react";
import { getStatusOptions } from "./Components/getStatusOptions";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  LinearProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Slider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import "../../../Styles/Create/Create.css";

// 🟣 Firebase Storage
import { storage } from "../../../Services/Storage";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

// 🟣 Cropper (même que mini-paris) — ajuste le chemin si besoin
import Cropper from "react-easy-crop";
import getCroppedImg from "../../Admin/cropImage";

const CreateBets = () => {
  // États
  const [step, setStep] = useState(1);
  const [team1, setTeam1] = useState("");
  const [team2, setTeam2] = useState("");
  const [odds, setOdds] = useState({ winTeam1: "", draw: "", winTeam2: "" });
  const [matchStatus, setMatchStatus] = useState("à venir");
  const [score] = useState({ team1: 0, team2: 0 });
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [sportCategory, setSportCategory] = useState("");
  const [statusOptions, setStatusOptions] = useState([]);
  // eslint-disable-next-line
  const [bettingOpen, setBettingOpen] = useState(false);

  const idOrganisation = localStorage.getItem("idOrganisation");
  const navigate = useNavigate();

  // 🟣 ÉTATS image (flow comme mini-paris)
  const [rawImageFile, setRawImageFile] = useState(null); // fichier sélectionné
  const [imageFile, setImageFile] = useState(null); // fichier final (croppé)
  const [imageSrc, setImageSrc] = useState(""); // objectURL pour le cropper
  const [imagePreviewURL, setImagePreviewURL] = useState(""); // preview à l’écran
  const [imageUploadURL, setImageUploadURL] = useState(""); // URL Firebase finale
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // 🟣 Cropper states
  const [openCropper, setOpenCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropAreaPixels, setCropAreaPixels] = useState(null);

  // Étapes
  const handleNextStep = () => setStep((prev) => Math.min(prev + 1, 4));
  const handlePrevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  // Helpers & validation cotes > 1.01
  const toNum = (v) => {
    if (v === null || v === undefined || v === "") return NaN;
    return parseFloat(String(v).replace(",", ".")); // tolère virgule
  };
  const oddsNum = {
    winTeam1: toNum(odds.winTeam1),
    draw: toNum(odds.draw),
    winTeam2: toNum(odds.winTeam2),
  };
  const oddsAllPresent =
    odds.winTeam1 !== "" && odds.draw !== "" && odds.winTeam2 !== "";
  const oddsStrictlyValid = [
    oddsNum.winTeam1,
    oddsNum.draw,
    oddsNum.winTeam2,
  ].every((n) => !Number.isNaN(n) && n > 1.01);

  // Image : a-t-on commencé ?
  const hasStartedImage = Boolean(rawImageFile || imagePreviewURL || imageSrc);

  // Validation par étape
  const canProceedToNextStep = () => {
    if (step === 1) return sportCategory && team1 && team2;

    if (step === 2) {
      // toutes les cotes saisies + matchStatus + chaque cote > 1.01
      return oddsAllPresent && matchStatus && oddsStrictlyValid;
    }

    if (step === 3) {
      // date & heure obligatoires ET image passable UNIQUEMENT si rien n'est commencé
      return Boolean(date && time && (!hasStartedImage || imageUploadURL));
    }

    if (step === 4) {
      // revalide tout + cotes > 1.01
      return (
        sportCategory &&
        team1 &&
        team2 &&
        oddsAllPresent &&
        oddsStrictlyValid &&
        matchStatus &&
        date &&
        time
      );
    }
    return false;
  };

  // 🟣 Sélection d'image → ouvre le cropper
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (imagePreviewURL) URL.revokeObjectURL(imagePreviewURL);
    if (imageSrc) URL.revokeObjectURL(imageSrc);

    setRawImageFile(file);
    setImageFile(null);
    setImageUploadURL("");
    setUploadProgress(0);

    const url = URL.createObjectURL(file);
    setImageSrc(url);
    setOpenCropper(true);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setAlertMessage("");
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCropAreaPixels(croppedPixels);
  }, []);

  const handleCropCancel = () => {
    // si annule, on garde l’original en preview (permet d’uploader malgré tout)
    if (imagePreviewURL) URL.revokeObjectURL(imagePreviewURL);
    const preview = imageSrc || "";
    setImagePreviewURL(preview);
    setOpenCropper(false);
  };

  const handleCropConfirm = async () => {
    if (!imageSrc || !cropAreaPixels) return;
    const croppedBlob = await getCroppedImg(imageSrc, cropAreaPixels);
    const finalFile = new File([croppedBlob], `${Date.now()}_bet.jpg`, {
      type: "image/jpeg",
    });

    if (imagePreviewURL) URL.revokeObjectURL(imagePreviewURL);
    const previewURL = URL.createObjectURL(finalFile);
    setImageFile(finalFile);
    setImagePreviewURL(previewURL);
    setOpenCropper(false);
  };

  // 🟣 Upload vers Firebase Storage (croppé si dispo, sinon brut)
  const handleUploadImage = () => {
    const fileToUpload = imageFile || rawImageFile;
    if (!fileToUpload) {
      setAlertMessage("Sélectionnez (et recadrez) une image avant l’upload.");
      setAlertSeverity("error");
      return;
    }

    const ext =
      fileToUpload.name.split(".").pop()?.toLowerCase() ||
      (fileToUpload.type.includes("png") ? "png" : "jpg");
    const safeTeams = `${team1 || "team1"}-${team2 || "team2"}`
      .replace(/[^a-z0-9-_]+/gi, "_")
      .toLowerCase();
    const fileName = `${Date.now()}_${safeTeams}.${ext}`;

    const storageRef = ref(
      storage,
      `bet-images/${idOrganisation || "org"}/${fileName}`
    );
    const uploadTask = uploadBytesResumable(storageRef, fileToUpload, {
      contentType: fileToUpload.type,
    });

    setUploadingImage(true);
    setAlertMessage("");

    uploadTask.on(
      "state_changed",
      (snap) => {
        const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
        setUploadProgress(pct);
      },
      (error) => {
        console.error(error);
        setUploadingImage(false);
        setAlertMessage("Erreur lors de l’upload de l’image.");
        setAlertSeverity("error");
      },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        setImageUploadURL(url);
        setUploadingImage(false);
        setAlertMessage("Image téléchargée avec succès !");
        setAlertSeverity("success");
      }
    );
  };

  // Soumission finale
  const handleSubmit = async (e) => {
    e.preventDefault();

    const betsData = {
      path: `organisations/${idOrganisation}/bets`,
      data: {
        team1,
        team2,
        odds,
        matchStatus,
        score,
        date,
        time,
        sportCategory,
        bettingOpen,
        distributeWinningDone: false,
        imageBetUrl: imageUploadURL || "", // optionnelle
      },
    };

    try {
      const response = await fetch(
        "https://backend-betkiff.vercel.app/bets/addBets",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(betsData),
        }
      );

      const result = await response.text();
      setAlertMessage(result);
      setAlertSeverity("success");
      navigate("/");
    } catch (error) {
      setAlertMessage("Erreur lors de la création du pari.");
      setAlertSeverity("error");
    }
  };

  useEffect(() => {
    setStatusOptions(getStatusOptions(sportCategory));
  }, [sportCategory]);

  // cleanup objectURLs
  useEffect(() => {
    return () => {
      if (imagePreviewURL) URL.revokeObjectURL(imagePreviewURL);
      if (imageSrc) URL.revokeObjectURL(imageSrc);
    };
  }, [imagePreviewURL, imageSrc]);

  // Progress
  const progressValue = (step / 4) * 100;

  // UI
  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
      <Container maxWidth="sm" className="create-container">
        <Box
          sx={{
            position: "absolute",
            top: "0",
            left: "0",
            width: "100%",
            display: "flex",
            alignItems: "center",
            backgroundColor: "white",
            padding: "2vh",
            boxSizing: "border-box",
          }}>
          {/* Rond de progression */}
          <Box
            sx={{
              position: "relative",
              width: "70px",
              height: "70px",
              marginRight: "10px",
            }}>
            <CircularProgress
              variant="determinate"
              value={100}
              size={70}
              thickness={5}
              sx={{ color: "rgba(90, 20, 121, 0.3)", position: "absolute" }}
            />
            <CircularProgress
              variant="determinate"
              value={progressValue}
              size={70}
              thickness={5}
              sx={{ color: "#5A147E" }}
            />
            <Typography
              sx={{
                position: "absolute",
                fontSize: "16px",
                fontWeight: "bold",
                color: "#5A147E",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}>
              {step}/4
            </Typography>
          </Box>

          {/* Titre */}
          <Box>
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: "bold",
                color: "rgba(90, 20, 121, 254)",
                pl: "2vw",
              }}>
              {step === 1 && "Étape 1"}
              {step === 2 && "Étape 2"}
              {step === 3 && "Étape 3"}
              {step === 4 && "Étape 4"}
            </Typography>
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: "bold",
                color: "rgba(90, 20, 121, 254)",
                pl: "2vw",
                fontSize: "1rem",
              }}>
              {step === 1 && "Informations sur les Équipes"}
              {step === 2 && "Côtes et Statut"}
              {step === 3 && "Date, Heure & Image"}
              {step === 4 && "Vérification et Création"}
            </Typography>
          </Box>
        </Box>

        {alertMessage && (
          <Alert severity={alertSeverity} className="create-alert">
            {alertMessage}
          </Alert>
        )}

        {/* Étape 1 */}
        {step === 1 && (
          <form className="create-form">
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Catégorie du Sport</InputLabel>
                <Select
                  value={sportCategory}
                  onChange={(e) => setSportCategory(e.target.value)}
                  label="Catégorie du Sport">
                  <MenuItem value="football">Football</MenuItem>
                  <MenuItem value="basketball">Basketball</MenuItem>
                  <MenuItem value="rugby">Rugby</MenuItem>
                  <MenuItem value="coinche">Coinche</MenuItem>
                  <MenuItem value="spikeball">Spikeball</MenuItem>
                  <MenuItem value="handball">Handball</MenuItem>
                  <MenuItem value="volley">Volley</MenuItem>
                  <MenuItem value="octopearl">OctoPearl</MenuItem>
                  <MenuItem value="poulparty">Poulparty</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <TextField
              label="Équipe 1"
              value={team1}
              onChange={(e) => setTeam1(e.target.value)}
              variant="outlined"
              fullWidth
              sx={{ marginBottom: 2 }}
            />
            <TextField
              label="Équipe 2"
              value={team2}
              onChange={(e) => setTeam2(e.target.value)}
              variant="outlined"
              fullWidth
              sx={{ marginBottom: 2 }}
            />
          </form>
        )}

        {/* Étape 2 — Cotes & Statut */}
        {step === 2 && (
          <form className="create-form">
            <Grid container spacing={3}>
              <Grid item xs={4}>
                <TextField
                  label={`Cote ${team1 || "1"}`}
                  value={odds.winTeam1}
                  onChange={(e) =>
                    setOdds({ ...odds, winTeam1: e.target.value })
                  }
                  variant="outlined"
                  type="number"
                  fullWidth
                  inputProps={{ min: 1.02, step: 0.01 }}
                  error={odds.winTeam1 !== "" && !(oddsNum.winTeam1 > 1.01)}
                  helperText={
                    odds.winTeam1 !== "" && !(oddsNum.winTeam1 > 1.01)
                      ? "La cote doit être > 1.01"
                      : " "
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Cote Nul"
                  value={odds.draw}
                  onChange={(e) => setOdds({ ...odds, draw: e.target.value })}
                  variant="outlined"
                  type="number"
                  fullWidth
                  inputProps={{ min: 1.02, step: 0.01 }}
                  error={odds.draw !== "" && !(oddsNum.draw > 1.01)}
                  helperText={
                    odds.draw !== "" && !(oddsNum.draw > 1.01)
                      ? "La cote doit être > 1.01"
                      : " "
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label={`Cote ${team2 || "2"}`}
                  value={odds.winTeam2}
                  onChange={(e) =>
                    setOdds({ ...odds, winTeam2: e.target.value })
                  }
                  variant="outlined"
                  type="number"
                  fullWidth
                  inputProps={{ min: 1.02, step: 0.01 }}
                  error={odds.winTeam2 !== "" && !(oddsNum.winTeam2 > 1.01)}
                  helperText={
                    odds.winTeam2 !== "" && !(oddsNum.winTeam2 > 1.01)
                      ? "La cote doit être > 1.01"
                      : " "
                  }
                />
              </Grid>
            </Grid>

            <FormControl fullWidth sx={{ marginTop: 2 }}>
              <InputLabel>Statut du Match</InputLabel>
              <Select
                value={matchStatus}
                onChange={(e) => setMatchStatus(e.target.value)}
                label="Statut du Match">
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </form>
        )}

        {/* Étape 3 : Date/Heure + Image (optionnelle & passable si non commencée) */}
        {step === 3 && (
          <form className="create-form">
            <TextField
              label="Date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              variant="outlined"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ marginBottom: 2 }}
            />
            <TextField
              label="Heure"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              variant="outlined"
              type="time"
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ marginBottom: 2 }}
            />

            {/* Bloc Image */}
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              Image du match (optionnelle)
            </Typography>

            <Box
              sx={{
                width: "100%",
                height: 200,
                borderRadius: "10px",
                border: "2px dashed #ccc",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                mb: 1.5,
              }}>
              {imagePreviewURL || imageUploadURL ? (
                <img
                  src={imageUploadURL || imagePreviewURL}
                  alt="Aperçu"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <Typography variant="body2" color="gray">
                  Aperçu de l’image
                </Typography>
              )}
            </Box>

            {!imageUploadURL && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}>
                {!rawImageFile ? (
                  <>
                    <Button variant="outlined" component="label">
                      Sélectionner une image
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleImageSelect}
                      />
                    </Button>
                    {/* Passer uniquement si rien n'a été commencé */}
                    <Button
                      onClick={handleNextStep}
                      variant="text"
                      sx={{ flexGrow: 1 }}>
                      Passer
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="text" onClick={() => setOpenCropper(true)}>
                      Recadrer à nouveau
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleUploadImage}
                      disabled={!(imageFile || rawImageFile) || uploadingImage}>
                      {uploadingImage ? "Upload en cours…" : "Uploader l’image"}
                    </Button>
                  </>
                )}
              </Box>
            )}

            {uploadingImage && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption">{uploadProgress}%</Typography>
              </Box>
            )}

            {imageUploadURL && (
              <Typography variant="body2" sx={{ mt: 1 }} color="success.main">
                Image prête ✅
              </Typography>
            )}

            {/* Aide si l'image est commencée mais pas uploadée */}
            {hasStartedImage && !imageUploadURL && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: "block" }}>
                Vous avez sélectionné une image : uploadez-la pour continuer.
              </Typography>
            )}
          </form>
        )}

        {/* Étape 4 : Preview finale */}
        {step === 4 && (
          <Card
            sx={{
              maxWidth: 400,
              borderRadius: 5,
              overflow: "hidden",
              boxShadow: 3,
              backgroundColor: "#F8F0FC",
            }}>
            {/* Bande supérieure avec la date */}
            <Box
              sx={{
                backgroundColor: "#6A1B9A",
                padding: "10px",
                textAlign: "center",
                color: "white",
              }}>
              <Typography variant="body2">
                {new Date(`${date}T${time}`).toLocaleString("fr-FR", {
                  weekday: "short",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Typography>
            </Box>

            {/* Image (si dispo) */}
            {imageUploadURL && (
              <img
                src={imageUploadURL}
                alt={`Visuel ${team1} - ${team2}`}
                style={{ width: "100%", height: 150, objectFit: "cover" }}
              />
            )}

            <CardContent sx={{ textAlign: "center" }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "#4A148C", mb: 1 }}>
                {team1} - {team2}
              </Typography>
              <Typography variant="body2" sx={{ color: "#757575" }}>
                {matchStatus} | Score : 0 - 0
              </Typography>

              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={4}>
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      backgroundColor: "#E1BEE7",
                      color: "#4A148C",
                      fontWeight: "bold",
                    }}>
                    {team1}
                    <br />
                    {odds.winTeam1}
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      backgroundColor: "#E1BEE7",
                      color: "#4A148C",
                      fontWeight: "bold",
                    }}>
                    ÉGALITÉ
                    <br />
                    {odds.draw}
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      backgroundColor: "#E1BEE7",
                      color: "#4A148C",
                      fontWeight: "bold",
                    }}>
                    {team2}
                    <br />
                    {odds.winTeam2}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Boutons de navigation */}
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            display: "flex",
            padding: "2vh",
            width: "100%",
            boxSizing: "border-box",
            backgroundColor: "white",
            gap: 2,
          }}>
          {step === 1 && (
            <Button
              onClick={() => navigate("/dashboard")}
              variant="outlined"
              sx={{
                flexGrow: 1,
                color: "rgba(90, 20, 121, 254)",
                fontWeight: "bold",
                border: "1px solid rgba(90, 20, 121, 254)",
              }}>
              Dashboard
            </Button>
          )}
          {step > 1 && (
            <Button
              onClick={handlePrevStep}
              variant="outlined"
              sx={{
                flexGrow: 1,
                color: "rgba(90, 20, 121, 254)",
                fontWeight: "bold",
                border: "1px solid rgba(90, 20, 121, 254)",
              }}>
              Retour
            </Button>
          )}
          {step < 4 ? (
            <Button
              onClick={handleNextStep}
              variant="contained"
              sx={{
                flexGrow: 1,
                backgroundColor: "rgba(90, 20, 121, 254)",
                fontWeight: "bold",
                color: "white",
              }}
              disabled={!canProceedToNextStep()}>
              Suivant
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              variant="contained"
              sx={{
                flexGrow: 1,
                backgroundColor: "rgba(90, 20, 121, 254)",
                fontWeight: "bold",
                color: "white",
              }}
              disabled={!canProceedToNextStep()}>
              Créer le Pari
            </Button>
          )}
        </Box>
      </Container>

      {/* 🔳 Dialog de recadrage (même UX que mini-paris) */}
      <Dialog
        open={openCropper}
        onClose={handleCropCancel}
        fullWidth
        maxWidth="sm"
        sx={{ "& .MuiBackdrop-root": { backgroundColor: "rgba(0,0,0,0.8)" } }}>
        <DialogContent sx={{ backgroundColor: "#222", color: "#fff" }}>
          <Typography variant="h6" textAlign="center" sx={{ mb: 2 }}>
            Recadrez votre image
          </Typography>
          <Box sx={{ position: "relative", width: "100%", height: 400 }}>
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              // Ratio proche de l'affichage (100% x 150px en cover)
              aspect={16 / 9}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              restrictPosition
              showGrid={false}
            />
          </Box>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(_, v) => setZoom(v)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCropCancel} color="secondary">
            Annuler
          </Button>
          <Button
            onClick={handleCropConfirm}
            variant="contained"
            color="primary">
            Valider le recadrage
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CreateBets;
